import { useEffect, useRef } from "react";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { shouldShowMessageTime } from "@/core/utils/message";
import { MessageDisplay } from "./components/message-display";
import { TEXT_MESSAGE_DIRECTIONS } from "./constants";
import { useGetCustomerTextMessages } from "./query";
import { IMostRecentTextMessage } from "./text-messages";

interface TextMessagesSidebarProps {
  textMessage?: IMostRecentTextMessage | null;
  isOpen: boolean;
}

const TextMessagesSidebar = ({
  textMessage,
  isOpen,
}: TextMessagesSidebarProps) => {
  const { data: messages, isLoading } = useGetCustomerTextMessages(
    textMessage?.customer.id,
  );

  const viewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  if (isLoading) {
    return (
      <Stack gap="lg" className="p-6" align="end">
        <Skeleton className="h-16 w-[330px]" />
        <Skeleton className="h-40 w-[330px]" />
        <Skeleton className="h-12 w-[330px]" />

        <Skeleton className="h-16 w-[330px] self-start" />
      </Stack>
    );
  }

  return (
    <ScrollArea
      viewportRef={viewportRef}
      className="h-full w-full pr-2"
      aria-label="Text message conversation"
      role="log"
      aria-live="polite"
    >
      <Stack gap="sm" className="pb-14 pl-6 pr-4 pt-6">
        {messages?.map((message, idx) => (
          <MessageDisplay
            key={message.id}
            message={message.body}
            mediaUrls={message.media_urls}
            isOutbound={message.direction === TEXT_MESSAGE_DIRECTIONS.outbound}
            createdAt={message.created_at}
            // TODO consider enriching messages with this field on load rather than calculating each render
            showTime={shouldShowMessageTime(message, messages[idx + 1])}
          />
        ))}
      </Stack>
    </ScrollArea>
  );
};

export default TextMessagesSidebar;
